import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { readContract } from '@wagmi/core';
import { DEFAULT_WAGMI_QUERY_ASSET_PRICE_STALE_TIME } from '@/onchain/querySettings';
import { wagmiConfig } from '@/onchain/OnchainProviders';
import { stateViewAbi, stateViewAddress } from '@generated/generated';
import { getPool } from '@/onchain/pools';
import { tickToPriceScaled } from '@/utils/tickMath';
import { assetBySymbol } from '@/onchain/assets';
import { getCurrentEnvironment, Environment } from '@/onchain/store/environment';

// There is an issue getting the cbBTC price from the Coinbase API, so we need to override it
// for now. I've placed this logic here because it's specific to this price oracle
// and if we use a different provider we may not need this.
const TICKER_OVERRIDES = {
  CBBTC: 'BTC',
};

// tEquity tokens that should get prices from pools instead of Coinbase API
const TEQUITY_TOKENS = ['tABC', 'tLMN', 'tXYZ', 'tCOIN'];

// Function to check if a token is a tEquity token and should use pool pricing
const isTEquityToken = (assetSymbol: string): boolean => {
  const currentEnv = getCurrentEnvironment();
  const isDevEnvironment =
    currentEnv === Environment.development || currentEnv === Environment.localhost;
  return isDevEnvironment && TEQUITY_TOKENS.includes(assetSymbol);
};

// Function to get tEquity token price from its USDC pool
const getTEquityTokenPrice = async (assetSymbol: string, chainId: number): Promise<number> => {
  try {
    // Get the pool for this tEquity token paired with USDC
    const pool = getPool(chainId, assetSymbol, 'USDC');

    if (!pool) {
      throw new Error(`Pool not found for ${assetSymbol}/USDC on chain ${chainId}`);
    }

    // Get the current pool state to extract price
    const poolState = await readContract(wagmiConfig, {
      address: stateViewAddress[chainId as keyof typeof stateViewAddress],
      abi: stateViewAbi,
      functionName: 'getSlot0',
      args: [pool.keyBytes32],
    });

    if (!poolState || !Array.isArray(poolState) || poolState.length < 2) {
      throw new Error(`Invalid pool state for ${assetSymbol}/USDC on chain ${chainId}`);
    }

    const tick = Number(poolState[1]);

    // Get asset configurations to determine token order and decimals
    const tEquityAsset = assetBySymbol(assetSymbol);
    const usdcAsset = assetBySymbol('USDC');

    if (!tEquityAsset || !usdcAsset) {
      throw new Error(`Asset configuration not found for ${assetSymbol} or USDC`);
    }

    // Calculate the price using tick data
    // The price from tickToPriceScaled gives us the price scaled by 1e18
    const priceScaled = tickToPriceScaled(tick);

    // Determine if tEquity token is token0 or token1 in the pool
    const tEquityAddress = tEquityAsset.address[chainId];
    const usdcAddress = usdcAsset.address[chainId];
    const isToken0TEquity = tEquityAddress.toLowerCase() < usdcAddress.toLowerCase();

    // Convert from scaled bigint to number (priceScaled is scaled by 1e18)
    const priceNumber = Number(priceScaled) / Math.pow(10, 18);

    // Adjust for token decimals and position in pool
    const decimalAdjustment = Math.pow(10, usdcAsset.exponent - tEquityAsset.exponent);

    let usdPrice: number;
    if (isToken0TEquity) {
      // tEquity is token0, USDC is token1
      // Price is USDC per tEquity, so this is already the USD price
      usdPrice = priceNumber * decimalAdjustment;
    } else {
      // USDC is token0, tEquity is token1
      // Price is tEquity per USDC, so we need to invert it
      usdPrice = decimalAdjustment / priceNumber;
    }

    // Ensure we return a reasonable price (basic sanity check)
    if (usdPrice <= 0 || !isFinite(usdPrice)) {
      throw new Error(`Invalid calculated price for ${assetSymbol}: ${usdPrice}`);
    }
    return usdPrice;
  } catch (error) {
    console.error(`Error fetching pool price for ${assetSymbol}:`, error);
    // Re-throw the error instead of hiding it with a fallback value
    // This allows the caller to handle the error appropriately
    throw error;
  }
};

export type AssetUSDPriceMap<T extends readonly string[]> = Record<T[number], number>;

const getCoinbaseSpotPrice = async (
  baseSymbol: string,
  quoteSymbol: 'USD' | 'USDC',
): Promise<number> => {
  const resp: Response = await fetch(
    `https://api.coinbase.com/v2/prices/${baseSymbol}-${quoteSymbol}/spot`,
  );

  if (!resp.ok) {
    throw new Error(`Failed to fetch ${baseSymbol}-${quoteSymbol} spot price: ${resp.statusText}`);
  }

  const responseJson = (await resp.json()) as { data: { amount: string } };

  if (responseJson?.data?.amount == null) {
    throw new Error(`Failed to parse ${baseSymbol}-${quoteSymbol} spot price`);
  }

  const assetPrice = Number.parseFloat(String(responseJson.data.amount));

  if (!Number.isFinite(assetPrice)) {
    throw new Error(`Failed to parse ${baseSymbol}-${quoteSymbol} spot price`);
  }

  return assetPrice;
};

export const getAssetUSDPrice = async (assetSymbol: string): Promise<number> => {
  if (!assetSymbol) {
    throw new Error('Asset symbol is required');
  }

  // Check if this is a tEquity token that should use pool pricing
  if (isTEquityToken(assetSymbol)) {
    // For tEquity tokens, determine chainId based on environment
    // In dev environments, we use Base Sepolia where tEquity tokens are deployed
    const currentEnv = getCurrentEnvironment();
    if (currentEnv === Environment.development || currentEnv === Environment.localhost) {
      const chainId = 84532; // Base Sepolia
      return getTEquityTokenPrice(assetSymbol, chainId);
    } else {
      // tEquity tokens are only available in dev environments
      throw new Error(`tEquity token ${assetSymbol} is only available in development environments`);
    }
  }

  // For other tokens, use the original Coinbase API logic
  const symbolOverride =
    TICKER_OVERRIDES[assetSymbol.toUpperCase() as keyof typeof TICKER_OVERRIDES];
  const baseSymbol = symbolOverride || assetSymbol.toUpperCase();

  try {
    return await getCoinbaseSpotPrice(baseSymbol, 'USD');
  } catch (usdError) {
    try {
      // Fallback to USDC pair when USD pair intermittently fails.
      // We treat USDC as a USD proxy for this resilience path.
      return await getCoinbaseSpotPrice(baseSymbol, 'USDC');
    } catch (usdcError) {
      const usdErrorMessage = usdError instanceof Error ? usdError.message : String(usdError);
      const usdcErrorMessage = usdcError instanceof Error ? usdcError.message : String(usdcError);
      throw new Error(
        `Failed to fetch asset price for ${assetSymbol}. USD error: ${usdErrorMessage}. USDC fallback error: ${usdcErrorMessage}`,
      );
    }
  }
};

export const QUERY_KEY_ASSET_USD_PRICE = (assetSymbol: string) => ['useAssetUSDPrice', assetSymbol];

const QUERY_FN_ASSET_USD_PRICE = (assetSymbol: string) => async () => {
  return getAssetUSDPrice(assetSymbol);
};

/**
 * Fetches the USD price of a single token from the Coinbase API.
 * @param assetSymbol - The asset symbol to fetch the price for.
 * @returns The USD price of the token.
 * @throws Will throw a promise during initial load and while refreshing (React Suspense boundary required)
 */
export function useAssetUSDPrice(assetSymbol: string) {
  // Using useSuspenseQuery means this hook must be wrapped in a Suspense boundary
  // It will throw a promise while loading instead of returning a loading state

  const queryFn = useCallback(() => QUERY_FN_ASSET_USD_PRICE(assetSymbol), [assetSymbol]);

  const query = useSuspenseQuery({
    queryKey: QUERY_KEY_ASSET_USD_PRICE(assetSymbol),
    queryFn,
    // Cache asset prices for 1 minute and reduce refetching
    staleTime: DEFAULT_WAGMI_QUERY_ASSET_PRICE_STALE_TIME,
    gcTime: DEFAULT_WAGMI_QUERY_ASSET_PRICE_STALE_TIME * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return query;
}

/**
 * Fetches the USD prices of multiple tokens from the Coinbase API.
 * @param assetSymbols - The asset symbols to fetch the prices for.
 * @returns The USD prices of the tokens.
 * @throws Will throw a promise during initial load and while refreshing (React Suspense boundary required)
 */
export function useAssetUSDPrices<T extends string[]>(assetSymbols: T) {
  const combine = useCallback((results: { data: number }[]) => {
    return {
      data: results.map((result) => result?.data),
    };
  }, []);

  const queries = useMemo(
    () =>
      assetSymbols.map((assetSymbol) => ({
        queryKey: QUERY_KEY_ASSET_USD_PRICE(assetSymbol),
        queryFn: QUERY_FN_ASSET_USD_PRICE(assetSymbol),
      })),
    [assetSymbols],
  );

  const query = useSuspenseQueries({
    queries,
    combine,
  });

  const data = useMemo(
    () =>
      assetSymbols.reduce((acc, assetSymbol, i) => {
        acc[assetSymbol as T[number]] = query.data[i];
        return acc;
      }, {} as AssetUSDPriceMap<T>),
    [assetSymbols, query.data],
  );

  return { data };
}
